import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { GPS } from "../../util/geo";
import { CubeSections, Intermediate, RReport } from "../report/Report";
import { fCoords } from "../report/sections/util";
import { useWorkspaceCube, useWorkspaces } from "../state";
import { useOwnership } from "../state/ownership";

const WorkspaceReportData = ({ location }: { location: string }) => {
  const workspaceId = useMemo(() => {
    const n = parseInt(location, 10);
    return Number.isFinite(n) ? n : undefined;
  }, [location]);

  const [selectedWorkspaceId, cube, state, error, selectWorkspace] = useWorkspaceCube(
    useShallow((s) => [s.workspaceId, s.cube, s.state, s.error, s.selectWorkspace])
  );

  const workspace = useWorkspaces((s) => s.workspaces?.find((p) => p.id === workspaceId));
  const coords = useMemo(() => {
    const parcels = workspace?.parcels ?? [];
    if (!parcels.length) return undefined;
    return parcels.map((p) => fCoords(p.center.coordinates as GPS, ",")).join(",");
  }, [workspace]);

  useEffect(() => {
    if (workspaceId == null) return;
    if (selectedWorkspaceId !== workspaceId) selectWorkspace(workspaceId);
  }, [workspaceId, selectedWorkspaceId, selectWorkspace]);

  const ownershipRows = useOwnership(
    workspaceId != null ? `/api/workspaces/${workspaceId}/ownership` : undefined
  );

  if (workspaceId == null) return null;
  if (state !== "fetched" || !cube) {
    return <Intermediate state={state} cube={cube} error={error} />;
  }
  return (
    <CubeSections cube={cube} coords={coords} ownership={{ rows: ownershipRows, named: true }} />
  );
};

export const WorkspaceDetailView = () => {
  const { id } = useParams();
  const numericId = id ? parseInt(id, 10) : null;
  const selectWorkspace = useWorkspaces((s) => s.selectWorkspace);

  // Keep the ProjectBar in sync with the URL.
  useEffect(() => {
    selectWorkspace(numericId && Number.isFinite(numericId) ? numericId : null);
  }, [numericId, selectWorkspace]);

  if (!numericId || !Number.isFinite(numericId)) return null;
  return (
    <RReport
      location={String(numericId)}
      updateSection={() => {}}
      Component={WorkspaceReportData}
    />
  );
};
